/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, RefreshCw, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useEffect, useState } from 'react';

export function ChangelogPage() {
  const [changelogContent, setChangelogContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchChangelog = async () => {
      try {
        const response = await fetch('/api/changelog');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data?.content) {
            setChangelogContent(data.data.content);
          }
        }
      } catch (error) {
        console.error('Failed to fetch changelog:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchChangelog();
  }, []);

  const renderChangelogLine = (line: string, index: number) => {
    // Version headers like "## [0.7.5] - 2026-01-23"
    if (line.match(/^## \[[\d.]+\]/)) {
      return (
        <div key={index} className={`text-xl font-bold text-blue-700 ${index === 0 ? 'mt-0' : 'mt-8'} mb-4 pb-3 border-b-2 border-blue-500`}>
          {line.replace(/^## /, '')}
        </div>
      );
    }
    
    // Section headers like "### Velocity PPM"
    if (line.match(/^### /)) {
      return (
        <div key={index} className="text-lg font-semibold text-teal-600 mt-6 mb-3">
          {line.replace(/^### /, '')}
        </div>
      );
    }
    
    // Subheaders like "#### Something"
    if (line.match(/^#### /)) {
      return (
        <div key={index} className="text-base font-semibold text-indigo-600 mt-4 mb-2">
          {line.replace(/^#### /, '')}
        </div>
      );
    }
    
    // Main title
    if (line.match(/^# /)) {
      return (
        <div key={index} className="text-3xl font-bold text-slate-900 mb-6">
          {line.replace(/^# /, '')}
        </div>
      );
    }
    
    // Feature entries [Feature]
    if (line.includes('[Feature]')) {
      return (
        <div key={index} className="py-1.5 flex items-start gap-3">
          <span className="bg-green-100 text-green-800 px-2.5 py-0.5 rounded text-xs font-semibold flex-shrink-0">
            Feature
          </span>
          <span className="text-slate-700">{line.replace(/\[Feature\]\s*/, '')}</span>
        </div>
      );
    }
    
    // Bug fix entries [Bug Fix]
    if (line.includes('[Bug Fix]')) {
      return (
        <div key={index} className="py-1.5 flex items-start gap-3">
          <span className="bg-red-100 text-red-800 px-2.5 py-0.5 rounded text-xs font-semibold flex-shrink-0">
            Bug Fix
          </span>
          <span className="text-slate-700">{line.replace(/\[Bug Fix\]\s*/, '')}</span>
        </div>
      );
    }
    
    // Task entries [Task]
    if (line.includes('[Task]')) {
      return (
        <div key={index} className="py-1.5 flex items-start gap-3">
          <span className="bg-indigo-100 text-indigo-800 px-2.5 py-0.5 rounded text-xs font-semibold flex-shrink-0">
            Task
          </span>
          <span className="text-slate-700">{line.replace(/\[Task\]\s*/, '')}</span>
        </div>
      );
    }
    
    // List items starting with - or * with bold
    if (line.match(/^[\s]*[-*]\s+\*\*/)) {
      const parts = line.replace(/^[\s]*[-*]\s+/, '');
      return (
        <div 
          key={index} 
          className="py-1 pl-4 text-slate-700"
          dangerouslySetInnerHTML={{
            __html: parts.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-slate-900">$1</strong>')
          }} 
        />
      );
    }
    
    // Regular list items
    if (line.match(/^[\s]*[-*]\s+/)) {
      return (
        <div key={index} className="py-1 pl-4 text-slate-600">
          {line.replace(/^[\s]*[-*]\s+/, '• ')}
        </div>
      );
    }
    
    // Numbered list items
    if (line.match(/^\s*\d+\.\s+/)) {
      return (
        <div key={index} className="py-1 pl-4 text-slate-600">
          {line}
        </div>
      );
    }
    
    // Empty lines
    if (line.trim() === '') {
      return <div key={index} className="h-2" />;
    }
    
    // Code blocks
    if (line.trim().startsWith('```')) {
      return (
        <div key={index} className="bg-slate-800 text-slate-200 px-3 py-1 rounded font-mono text-sm">
          {line.trim() === '```' ? '' : line.replace(/^```\w*/, '')}
        </div>
      );
    }
    
    // Links at the bottom
    if (line.match(/^\[[\d.]+\]:/)) {
      return (
        <div key={index} className="text-xs text-slate-400 py-0.5">
          {line}
        </div>
      );
    }
    
    // Horizontal rules (---) - "Release Notes" and similar dividers
    if (line.match(/^---+$/)) {
      return <hr key={index} className="my-6 border-slate-200" />;
    }
    
    // Regular text
    return (
      <div key={index} className="py-0.5 text-slate-600">
        {line}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/home" className="flex items-center space-x-2">
              <Zap className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-slate-900">Velocity</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/home" className="text-slate-600 hover:text-slate-900 transition-colors">
                <Button variant="ghost">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Home
                </Button>
              </Link>
              <Link to="/home/download" className="text-slate-600 hover:text-slate-900 transition-colors">
                <Button variant="outline">Download</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <FileText className="h-12 w-12 text-blue-600" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Changelog
          </h1>
          <p className="text-xl text-slate-600 mb-8">
            All notable changes to Velocity PPM and the SkillShop Platform
          </p>
        </div>
      </section>

      {/* Changelog Content */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center py-20 text-slate-500">
                <RefreshCw className="h-6 w-6 animate-spin mr-3" />
                Loading changelog...
              </div>
            ) : changelogContent ? (
              <div className="p-6 md:p-8 max-h-[70vh] overflow-y-auto">
                <div className="font-mono text-sm leading-relaxed">
                  {changelogContent.split('\n').map((line, index) => renderChangelogLine(line, index))}
                </div>
              </div>
            ) : (
              <div className="py-20 text-center text-slate-500">
                Unable to load changelog
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="mt-8 bg-slate-50 rounded-xl p-6 border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Entry Types</h3>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <span className="bg-green-100 text-green-800 px-2.5 py-0.5 rounded text-xs font-semibold">Feature</span>
                <span className="text-sm text-slate-600">New functionality</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-red-100 text-red-800 px-2.5 py-0.5 rounded text-xs font-semibold">Bug Fix</span>
                <span className="text-sm text-slate-600">Problem resolved</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-indigo-100 text-indigo-800 px-2.5 py-0.5 rounded text-xs font-semibold">Task</span>
                <span className="text-sm text-slate-600">Maintenance & improvements</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to try the latest features?
          </h2>
          <p className="text-lg text-blue-100 mb-8">
            Download Velocity or sign up for the web version
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/home/download">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50">
                Download Now
              </Button>
            </Link>
            <Link to="/signup">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Try Web Version
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Zap className="h-6 w-6 text-blue-500" />
              <span className="text-xl font-bold text-white">Velocity</span>
            </div>
            <p className="text-sm">
              &copy; 2025 Lumavec Group. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
